import { Tooltip, makeStyles, mergeClasses, tokens } from '@fluentui/react-components';
import type { Relay } from '../api/types';
import type { CostView } from '../pages/overview/model';
import { vt } from '../theme';

const useStyles = makeStyles({
  bar: {
    height: '26px',
    flexShrink: 0,
    display: 'flex',
    alignItems: 'center',
    columnGap: '26px',
    paddingInline: '12px 14px',
    // The one gradient in the system (tokens.mjs ACCENT_BAR), the same in both themes.
    backgroundImage: 'linear-gradient(90deg, #0F6CBD 0%, #2F7FE0 50%, #5B6BEA 100%)',
    color: '#FFFFFF',
    fontSize: '11.5px',
    fontWeight: tokens.fontWeightMedium,
    whiteSpace: 'nowrap',
    overflow: 'hidden',
  },
  item: { display: 'inline-flex', alignItems: 'center', columnGap: '8px' },
  dot: {
    width: '8px',
    height: '8px',
    borderRadius: '50%',
    boxShadow: '0 0 0 1.5px rgba(0,0,0,0.3)',
    backgroundColor: vt.fg4,
  },
  live: { backgroundColor: vt.green },
  moving: { backgroundColor: vt.cyan },
  brandWord: { fontWeight: tokens.fontWeightSemibold, letterSpacing: '1.15px' },
  mono: { fontFamily: tokens.fontFamilyMonospace, fontSize: '11px' },
  spacer: { flexGrow: 1 },
  faint: { color: 'rgba(255,255,255,0.75)' },
});

/** "relay.vipper.network:51820" -> "relay.vipper.network". */
const host = (endpoint: string) => endpoint.replace(/:\d+$/, '');

/** The gradient foot: facts true of the whole session, never a per-screen control. */
export function StatusBar({ relay, cost }: { relay: Relay | null; cost: CostView }) {
  const s = useStyles();
  const state = relay?.state;
  const dot = state === 'running' ? s.live : state === 'starting' || state === 'stopping' ? s.moving : undefined;
  return (
    <footer className={s.bar} role="status" aria-label="Status">
      <span className={s.item}>
        <span className={mergeClasses(s.dot, dot)} aria-hidden />
        <span>
          <span className={s.brandWord}>VIPPER</span> · relay {state ?? 'unknown'}
        </span>
      </span>
      {relay && (
        <span className={s.mono}>
          {host(relay.endpoint)}
          {relay.publicIp && ` → ${relay.publicIp}`}
        </span>
      )}
      <span className={s.spacer} />
      <span className={s.item}>
        {cost.tooltip ? (
          <Tooltip content={cost.tooltip} relationship="description">
            <span className={s.mono} data-testid="cost">
              {cost.value} MTD{cost.cached && <span className={s.faint}> · cached</span>}
            </span>
          </Tooltip>
        ) : (
          <span className={s.mono} data-testid="cost">
            {cost.value} MTD
          </span>
        )}
      </span>
      <span className={s.mono}>v{__APP_VERSION__}</span>
    </footer>
  );
}
